import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import IgCard from "@/components/IgCard";
import { PhotoUploader } from "@/components/PhotoUploader";
import { catsOf } from "@/lib/catalog";
import { ColorEditor } from "@/components/ColorEditor";
import { InstagramPanel } from "@/components/InstagramPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const dayLabel = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { weekday: "short" }); } catch { return iso; } };
const hoursSince = (iso) => { try { return (Date.now() - new Date(iso).getTime()) / 3_600_000; } catch { return 0; } };
const waNudge = (order) => `https://wa.me/91${(order.customer?.mobile || "").replace(/\D/g,"").slice(-10)}?text=${encodeURIComponent(`Hi ${(order.customer?.name || "there").split(" ")[0]}, this is Arisha Creations. We're holding your order ${order.id} for ${money(order.total)}. Complete payment on https://razorpay.me/@gkgarments9501?amount=${order.total} and reply here with a screenshot — we'll ship right away.`)}`;

const SHIPPING_STEPS = [
  { key: "processing", label: "Preparing" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

const CATEGORY_OPTIONS = ["Kurtis", "Anarkali", "Dresses", "Festive Wear"];
const emptyProduct = { name: "", categories: [], price: 0, original_price: 0, description: "", image: "", images: [], sizes: "S,M,L,XL", rating: 4.8 };

export default function Admin() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [tracking, setTracking] = useState({});
  const [editingProduct, setEditingProduct] = useState(null); // form state for add/edit
  const [couponDraft, setCouponDraft] = useState({ code: "", type: "percent", value: 10, min_order: 0, active: true });
  const [igFor, setIgFor] = useState(null);

  const load = useCallback(async (t) => {
    const [ordersRes, dashRes, productsRes, couponsRes] = await Promise.all([
      axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Token": t } }),
      axios.get(`${API}/admin/dashboard`, { headers: { "X-Admin-Token": t } }),
      axios.get(`${API}/admin/products`, { headers: { "X-Admin-Token": t } }),
      axios.get(`${API}/admin/coupons`, { headers: { "X-Admin-Token": t } }),
    ]);
    setOrders(ordersRes.data); setDashboard(dashRes.data); setProducts(productsRes.data); setCoupons(couponsRes.data);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try { await axios.post(`${API}/admin/login`, { token }); await load(token); setAuthed(true); }
    catch (err) { setError(err?.response?.status === 401 ? "Invalid admin password." : "Login failed."); }
  };

  const updatePayment = async (id, payment_status) => {
    setBusy(id + payment_status);
    try { await axios.post(`${API}/admin/orders/${id}/status`, { token, payment_status }); await load(token); } finally { setBusy(""); }
  };
  const updateShipping = async (id, shipping_status, extra = {}) => {
    setBusy(id + shipping_status);
    try { await axios.post(`${API}/admin/orders/${id}/shipping`, { token, shipping_status, ...extra }); await load(token); } finally { setBusy(""); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    const draft = editingProduct;
    const body = {
      token,
      name: draft.name,
      categories: draft.categories || [],
      price: Number(draft.price),
      original_price: Number(draft.original_price),
      description: draft.description || "",
      image: draft.image,
      images: draft.images || [],
      colors: (draft.colors || []).map(c => ({ name: c.name.trim(), image: c.image || "" })).filter(c => c.name),
      sizes: (draft.sizes || "").split(",").map(s => s.trim()).filter(Boolean),
      rating: Number(draft.rating) || 4.8,
    };
    setBusy("product-save");
    try {
      if (draft.id) await axios.patch(`${API}/admin/products/${draft.id}`, body);
      else await axios.post(`${API}/admin/products`, body);
      setEditingProduct(null);
      await load(token);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not save. Check the fields.");
    } finally { setBusy(""); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    setBusy("del" + id);
    try { await axios.post(`${API}/admin/products/${id}/delete`, { token }); await load(token); } finally { setBusy(""); }
  };
  const editProduct = (p) => setEditingProduct({ ...p, categories: catsOf(p), sizes: (p.sizes || []).join(",") });
  const newProduct = () => setEditingProduct({ ...emptyProduct });

  const saveCoupon = async (e) => {
    e.preventDefault();
    setBusy("coupon-save");
    try {
      await axios.post(`${API}/admin/coupons`, { token, ...couponDraft, value: Number(couponDraft.value), min_order: Number(couponDraft.min_order) });
      setCouponDraft({ code: "", type: "percent", value: 10, min_order: 0, active: true });
      await load(token);
    } catch (err) { alert(err?.response?.data?.detail || "Could not save coupon."); }
    finally { setBusy(""); }
  };
  const deleteCoupon = async (code) => {
    if (!window.confirm(`Delete code ${code}?`)) return;
    await axios.post(`${API}/admin/coupons/${code}/delete`, { token });
    await load(token);
  };

  useEffect(() => { document.title = "Arisha Creations · Admin"; }, []);

  if (!authed) return (
    <div className="admin-wrap">
      <div className="admin-login" data-testid="admin-login">
        <p className="eyebrow">ARISHA CREATIONS</p>
        <h1>Admin sign in</h1>
        <form onSubmit={login}>
          <input type="password" value={token} onChange={e=>setToken(e.target.value)} placeholder="Admin password" required data-testid="admin-token-input"/>
          <button className="primary-button full" type="submit" data-testid="admin-login-submit">Sign in →</button>
          {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
        </form>
      </div>
    </div>
  );

  const pending = orders.filter(o => o.payment_status !== "paid" && o.payment_status !== "cancelled");
  const paid = orders.filter(o => o.payment_status === "paid");
  const maxDay = Math.max(1, ...(dashboard?.daily || []).map(d => d.revenue));

  return (
    <div className="admin-wrap">
      <div className="admin-header">
        <div><p className="eyebrow">ARISHA CREATIONS</p><h1>Admin</h1></div>
        <div className="admin-tabs" data-testid="admin-tabs">
          <button className={tab==="orders"?"active":""} onClick={()=>setTab("orders")} data-testid="tab-orders">Orders</button>
          <button className={tab==="products"?"active":""} onClick={()=>setTab("products")} data-testid="tab-products">Products</button>
          <button className={tab==="coupons"?"active":""} onClick={()=>setTab("coupons")} data-testid="tab-coupons">Coupons</button>
          <button className={tab==="instagram"?"active":""} onClick={()=>setTab("instagram")} data-testid="tab-instagram">Instagram</button>
        </div>
        <button className="admin-refresh" onClick={()=>load(token)} data-testid="admin-refresh">Refresh</button>
      </div>

      {tab === "orders" && (<>
        {dashboard && (
          <section className="dashboard" data-testid="admin-dashboard">
            <div className="dashboard-kpis">
              <div className="kpi" data-testid="kpi-revenue"><small>Revenue · 7 days</small><strong>{money(dashboard.revenue_7d)}</strong></div>
              <div className="kpi" data-testid="kpi-orders"><small>Orders · 7 days</small><strong>{dashboard.orders_7d}</strong></div>
              <div className="kpi" data-testid="kpi-aov"><small>Avg order</small><strong>{money(dashboard.avg_order_value)}</strong></div>
              <div className={`kpi ${dashboard.awaiting_over_hour ? "warn" : ""}`} data-testid="kpi-stale"><small>Awaiting &gt; 1h</small><strong>{dashboard.awaiting_over_hour}</strong></div>
            </div>
            <div className="dashboard-columns">
              <div className="revenue-chart" data-testid="revenue-chart">
                <p className="lux-label">Revenue this week</p>
                <div className="chart-bars">
                  {dashboard.daily.map(d => (
                    <div key={d.date} className="chart-bar" title={`${d.date}: ${money(d.revenue)}`} data-testid={`chart-${d.date}`}>
                      <span style={{height: `${Math.max(4, (d.revenue / maxDay) * 100)}%`}}/>
                      <small>{dayLabel(d.date)}</small>
                      <em>{d.revenue ? money(d.revenue) : "—"}</em>
                    </div>
                  ))}
                </div>
              </div>
              <div className="top-products" data-testid="top-products">
                <p className="lux-label">Top pieces · 7 days</p>
                {dashboard.top_products.length === 0 ? <p className="admin-empty" style={{padding:"14px",fontSize:"12px"}}>No paid orders yet this week.</p> :
                  <ol>
                    {dashboard.top_products.map(p => (
                      <li key={p.name} data-testid={`top-${p.name}`}>
                        {p.image && <img src={p.image} alt={p.name}/>}
                        <div><span>{p.name}</span><small>{p.qty} sold</small></div>
                        <strong>{money(p.revenue)}</strong>
                      </li>
                    ))}
                  </ol>
                }
              </div>
            </div>
          </section>
        )}

        <section className="admin-section">
          <h2>Awaiting payment</h2>
          {pending.length === 0 ? <p className="admin-empty" data-testid="admin-no-pending">No pending payments. You are all caught up.</p> :
            <div className="admin-orders" data-testid="admin-pending-orders">
              {pending.map(o => {
                const stale = hoursSince(o.created_at) >= 1;
                return (
                  <article key={o.id} className={`admin-order ${stale ? "stale" : ""}`} data-testid={`admin-order-${o.id}`}>
                    <div className="admin-order-head">
                      <div>
                        <span className="admin-order-id" data-testid={`admin-order-id-${o.id}`}>{o.id}</span>
                        <small>{fmt(o.created_at)}{stale ? " · aged" : ""}</small>
                      </div>
                      <strong className="admin-total">{money(o.total)}</strong>
                    </div>
                    <div className="admin-customer">
                      <strong>{o.customer?.name}</strong>
                      <span>{o.customer?.mobile} · {o.customer?.email}</span>
                      <span>{o.customer?.address}, {o.customer?.pincode}</span>
                    </div>
                    <ul className="admin-items">
                      {(o.items || []).map((it, i) => (
                        <li key={`${it.id || it.name}-${it.size || i}`}><span>{it.name}</span><small>Size {it.size}{it.color ? ` · ${it.color}` : ""} · Qty {it.qty} · {money(it.price)}</small></li>
                      ))}
                    </ul>
                    {stale && (
                      <a className="wa-nudge" href={waNudge(o)} target="_blank" rel="noreferrer" data-testid={`nudge-${o.id}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.05 0C5.5 0 .18 5.32.18 11.87c0 2.09.55 4.13 1.59 5.93L0 24l6.36-1.66a11.85 11.85 0 0 0 5.69 1.45h.01c6.55 0 11.87-5.32 11.87-11.87 0-3.17-1.24-6.15-3.41-8.44Zm-8.47 18.24a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.77.99 1.01-3.67-.24-.38a9.85 9.85 0 0 1-1.51-5.2c0-5.45 4.43-9.88 9.88-9.88 5.44 0 9.87 4.43 9.87 9.88 0 5.45-4.43 9.87-9.85 9.87Z"/></svg>
                        WhatsApp payment reminder
                      </a>
                    )}
                    <div className="admin-actions">
                      <span className={`badge badge-${o.payment_status || "awaiting_payment"}`}>{o.payment_status || "awaiting_payment"}</span>
                      <div>
                        <button className="admin-danger" disabled={busy===o.id+"cancelled"} onClick={()=>updatePayment(o.id, "cancelled")} data-testid={`admin-cancel-${o.id}`}>Cancel</button>
                        <button className="primary-button" disabled={busy===o.id+"paid"} onClick={()=>updatePayment(o.id, "paid")} data-testid={`admin-mark-paid-${o.id}`}>Mark paid ✓</button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          }
        </section>

        <section className="admin-section">
          <h2>Fulfilment · Paid orders</h2>
          {paid.length === 0 ? <p className="admin-empty">No paid orders yet.</p> :
            <div className="admin-orders" data-testid="admin-paid-orders">
              {paid.map(o => {
                const step = o.shipping_status || "processing";
                const stepIdx = SHIPPING_STEPS.findIndex(s => s.key === step);
                return (
                  <article key={o.id} className="admin-order" data-testid={`admin-fulfil-${o.id}`}>
                    <div className="admin-order-head">
                      <div>
                        <span className="admin-order-id">{o.id}</span>
                        <small>{o.customer?.name} · {o.customer?.mobile}</small>
                      </div>
                      <strong className="admin-total">{money(o.total)}</strong>
                    </div>
                    <div className="fulfil-progress">
                      {SHIPPING_STEPS.map((s, i) => (
                        <div key={s.key} className={`fulfil-step ${i <= stepIdx ? "done" : ""} ${i === stepIdx ? "current" : ""}`}>
                          <span/><small>{s.label}</small>
                        </div>
                      ))}
                    </div>
                    {(step === "shipped" || step === "delivered") && <div className="tracking-display">Tracking: <strong>{o.tracking_number || "—"}</strong>{o.courier ? ` · ${o.courier}` : ""}</div>}
                    <div className="admin-actions" style={{flexWrap:"wrap"}}>
                      {step === "processing" && <button className="primary-button" disabled={busy===o.id+"packed"} onClick={()=>updateShipping(o.id, "packed")} data-testid={`ship-packed-${o.id}`}>Mark packed</button>}
                      {step === "packed" && (
                        <div className="ship-input-row">
                          <input placeholder="Tracking # (optional)" value={tracking[o.id]?.tn || ""} onChange={e=>setTracking(t=>({...t, [o.id]: {...t[o.id], tn: e.target.value}}))} data-testid={`ship-tracking-input-${o.id}`}/>
                          <input placeholder="Courier" value={tracking[o.id]?.c || ""} onChange={e=>setTracking(t=>({...t, [o.id]: {...t[o.id], c: e.target.value}}))} data-testid={`ship-courier-input-${o.id}`}/>
                          <button className="primary-button" disabled={busy===o.id+"shipped"} onClick={()=>updateShipping(o.id, "shipped", { tracking_number: tracking[o.id]?.tn, courier: tracking[o.id]?.c })} data-testid={`ship-shipped-${o.id}`}>Mark shipped</button>
                        </div>
                      )}
                      {step === "shipped" && <button className="primary-button" disabled={busy===o.id+"delivered"} onClick={()=>updateShipping(o.id, "delivered")} data-testid={`ship-delivered-${o.id}`}>Mark delivered</button>}
                      {step === "delivered" && <span className="badge badge-paid">Delivered ✓</span>}
                    </div>
                  </article>
                );
              })}
            </div>
          }
        </section>
      </>)}

      {tab === "products" && (
        <section className="admin-section" data-testid="admin-products-section">
          <div className="admin-section-head">
            <h2 style={{border:0,padding:0,margin:0}}>Catalogue ({products.length})</h2>
            {!editingProduct && <button className="primary-button" onClick={newProduct} data-testid="new-product-button">+ Add product</button>}
          </div>

          {editingProduct && (
            <form className="product-form" onSubmit={saveProduct} data-testid="product-form">
              <h3>{editingProduct.id ? `Editing ${editingProduct.id}` : "New product"}</h3>
              <div className="product-form-grid">
                <label className="field"><span>Name</span><input required value={editingProduct.name} onChange={e=>setEditingProduct(p=>({...p, name: e.target.value}))} data-testid="product-form-name" /></label>
                <div className="field"><span>Categories (tick all that apply)</span>
                  <div className="cat-checks" data-testid="product-form-categories">
                    {CATEGORY_OPTIONS.map(c => (
                      <label key={c} className="cat-check">
                        <input type="checkbox" checked={(editingProduct.categories || []).includes(c)} onChange={e=>setEditingProduct(p=>({...p, categories: e.target.checked ? [...(p.categories || []), c] : (p.categories || []).filter(x => x !== c)}))} data-testid={`product-form-category-${c.toLowerCase().replace(/ /g, "-")}`} /> {c}
                      </label>
                    ))}
                  </div>
                </div>
                <label className="field"><span>Price (₹)</span><input required type="number" min="1" value={editingProduct.price} onChange={e=>setEditingProduct(p=>({...p, price: e.target.value}))} data-testid="product-form-price" /></label>
                <label className="field"><span>Original price (₹)</span><input required type="number" min="1" value={editingProduct.original_price} onChange={e=>setEditingProduct(p=>({...p, original_price: e.target.value}))} data-testid="product-form-original" /></label>
                <label className="field"><span>Main image URL (or upload below)</span><input required value={editingProduct.image} onChange={e=>setEditingProduct(p=>({...p, image: e.target.value}))} placeholder="https://..." data-testid="product-form-image" /></label>
                <label className="field"><span>Sizes (comma-separated)</span><input value={editingProduct.sizes} onChange={e=>setEditingProduct(p=>({...p, sizes: e.target.value}))} placeholder="S,M,L,XL,XXL" data-testid="product-form-sizes" /></label>
                <label className="field"><span>Rating</span><input type="number" step="0.1" min="0" max="5" value={editingProduct.rating} onChange={e=>setEditingProduct(p=>({...p, rating: e.target.value}))} data-testid="product-form-rating" /></label>
                <label className="field full-row"><span>Description</span><textarea rows={2} value={editingProduct.description} onChange={e=>setEditingProduct(p=>({...p, description: e.target.value}))} data-testid="product-form-description" /></label>
                <PhotoUploader token={token} product={editingProduct} onChange={(patch)=>setEditingProduct(p=>({...p, ...patch}))} />
                <ColorEditor token={token} colors={editingProduct.colors || []} onChange={(colors)=>setEditingProduct(p=>({...p, colors}))} />
              </div>
              <div className="product-form-actions">
                <button type="button" className="admin-danger" onClick={()=>setEditingProduct(null)} data-testid="product-form-cancel">Cancel</button>
                <button type="submit" className="primary-button" disabled={busy==="product-save"} data-testid="product-form-save">{busy==="product-save"?"Saving…":"Save product"}</button>
              </div>
            </form>
          )}

          <div className="products-grid" data-testid="admin-products-grid">
            {products.map(p => (
              <article key={p.id} className="admin-product" data-testid={`admin-product-${p.id}`}>
                <img src={p.image} alt={p.name}/>
                <div className="admin-product-info">
                  <p className="lux-card-cat">{p.category}</p>
                  <h3>{p.name}</h3>
                  <p className="admin-product-price">{money(p.price)} <del>{money(p.original_price)}</del></p>
                  <small>Sizes: {(p.sizes || []).join(", ") || "—"}</small>
                </div>
                <div className="admin-product-actions">
                  <button onClick={()=>editProduct(p)} data-testid={`edit-${p.id}`}>Edit</button>
                  <button onClick={()=>setIgFor(p)} data-testid={`ig-${p.id}`}>Instagram card</button>
                  <button className="admin-danger" onClick={()=>deleteProduct(p.id)} disabled={busy==="del"+p.id} data-testid={`delete-${p.id}`}>Delete</button>
                </div>
              </article>
            ))}
          </div>

          {igFor && (
            <div className="overlay" onClick={()=>setIgFor(null)}>
              <div className="modal ig-modal" onClick={e=>e.stopPropagation()} data-testid="ig-modal">
                <div className="ig-modal-head">
                  <div><p className="lux-eyebrow dark">Instagram-ready</p><h3>{igFor.name}</h3></div>
                  <button className="admin-refresh" onClick={()=>setIgFor(null)} data-testid="ig-close">Close</button>
                </div>
                <IgCard product={igFor} />
                <small className="ig-hint">1080 × 1080 PNG · perfect for Instagram feed. Handle: <strong>{process.env.REACT_APP_INSTAGRAM_HANDLE || "@arishacreations"}</strong>. Update via <code>REACT_APP_INSTAGRAM_HANDLE</code> in frontend .env.</small>
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "instagram" && <InstagramPanel token={token} products={products} />}

      {tab === "coupons" && (
        <section className="admin-section" data-testid="admin-coupons-section">
          <div className="admin-section-head">
            <h2 style={{border:0,padding:0,margin:0}}>Promo codes ({coupons.length})</h2>
          </div>

          <form className="coupon-form" onSubmit={saveCoupon} data-testid="coupon-form">
            <input required placeholder="Code (e.g. WELCOME10)" value={couponDraft.code} onChange={e=>setCouponDraft(d=>({...d, code: e.target.value.toUpperCase()}))} data-testid="coupon-form-code" />
            <select value={couponDraft.type} onChange={e=>setCouponDraft(d=>({...d, type: e.target.value}))} data-testid="coupon-form-type">
              <option value="percent">% off</option>
              <option value="flat">₹ flat off</option>
            </select>
            <input required type="number" min="1" placeholder="Value" value={couponDraft.value} onChange={e=>setCouponDraft(d=>({...d, value: e.target.value}))} data-testid="coupon-form-value" />
            <input type="number" min="0" placeholder="Min order (₹)" value={couponDraft.min_order} onChange={e=>setCouponDraft(d=>({...d, min_order: e.target.value}))} data-testid="coupon-form-min" />
            <label className="coupon-active-toggle">
              <input type="checkbox" checked={couponDraft.active} onChange={e=>setCouponDraft(d=>({...d, active: e.target.checked}))} data-testid="coupon-form-active" /> Active
            </label>
            <button className="primary-button" type="submit" disabled={busy==="coupon-save"} data-testid="coupon-form-save">{busy==="coupon-save"?"Saving…":"Save code"}</button>
          </form>

          {coupons.length === 0 ? <p className="admin-empty">No coupons yet. Add your first above.</p> :
            <table className="admin-table" data-testid="coupons-table">
              <thead><tr><th>Code</th><th>Discount</th><th>Min order</th><th>Uses</th><th>Status</th><th/></tr></thead>
              <tbody>
                {coupons.map(c => (
                  <tr key={c.code} data-testid={`coupon-${c.code}`}>
                    <td><code>{c.code}</code></td>
                    <td>{c.type === "percent" ? `${c.value}%` : money(c.value)}</td>
                    <td>{c.min_order ? money(c.min_order) : "—"}</td>
                    <td>{c.uses || 0}</td>
                    <td><span className={`badge badge-${c.active ? "paid" : "cancelled"}`}>{c.active ? "Active" : "Off"}</span></td>
                    <td><button className="admin-link" onClick={()=>deleteCoupon(c.code)} data-testid={`coupon-delete-${c.code}`}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          }
        </section>
      )}
    </div>
  );
}
